import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryOnly } from 'components/utils/breakpoint'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'

const HeroSection = styled.div`
  padding: ${rem(40)} 0;

  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const HeroBody = styled.div`
  flex: 1 1 auto;

  ${mediaQueryDown.tablet} {
    max-width: ${rem(400)};
  }

  ${mediaQueryOnly.tablet} {
    margin-inline: auto;
  }
`

const PanelColumn = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: ${rem(400)};

  ${mediaQueryOnly.tablet} {
    margin-inline: auto;
  }
`

interface Props {
  panel: ReactNode
}

function LoyaltyPurchasePointsHeroSection(props: Props) {
  const { panel } = props
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)

  const heading = intl.formatMessage({
    id: '8PY9LRMUH6-9aI0teTdX',
    defaultMessage: 'Top up your Société Points',
    description: 'Hero heading on the Société Buy Points landing page',
  })

  const subhead = intl.formatMessage({
    id: 'dsUfJQ8Gv9NuFVf85uuo',
    defaultMessage: 'Buy more points and start redeeming sooner – on stays, flights and more.',
    description: 'Body copy on the buy-points CTA card shown on the Société account benefits tab',
  })

  const promoLine = intl.formatMessage({
    id: 'XDgrgp92sHQRMonW-j_A',
    defaultMessage: '20% more points for a limited time only',
    description: 'Hero promo tagline on the Société Buy Points landing page (campaign messaging)',
  })

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <Group direction="vertical" desktopDirection="horizontal" gap={32} desktopGap={40} verticalAlign="center">
          <HeroBody>
            <Group direction="vertical" gap={20} horizontalAlign="start">
              {isInfiniteTier && <LoyaltyInfiniteBrandLogo height={28} />}
              {!isInfiniteTier && <LoyaltyProductLogo height={32} />}
              <VerticalSpacer gap={8}>
                <HeadingTextBlock variant="heading2">{heading}</HeadingTextBlock>
                <BodyTextBlock variant="large">{subhead}</BodyTextBlock>
              </VerticalSpacer>
              <MessageBanner kind="promotional-primary" density="compact" icon={<LineTagAltIcon />}>
                {promoLine}
              </MessageBanner>
            </Group>
          </HeroBody>
          <PanelColumn>{panel}</PanelColumn>
        </Group>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyPurchasePointsHeroSection
